import React, { ChangeEvent, Children, useId, useState } from 'react'
import styled from 'styled-components'
import { Feedback } from '../feedback'
import { Label } from '../label'
import { MarkdownPreview } from '../markdown-preview'
import { StrenghtMeter } from '../strenght-meter'
import { FormFieldProps } from './form-field.types'

export const StyledFormField = styled.div`
  ${({ theme }) =>
    `
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    line-height: 20px;
    gap: ${theme.sizes[2]};
    `}
`

export const FormField = ({
  status,
  description,
  disabled,
  label,
  necessity,
  showStrenghtMeter,
  children,
  statusMessage,
  'data-testid': dataTestid,
  descriptionVariant,
  hideCharacterCount,
  optionalText,
  instructions,
}: FormFieldProps) => {
  const child = Children.only(children)

  const fieldId = useId()
  const hintId = useId()
  const instructionsId = useId()
  const [trackedValue, setTrackedValue] = useState(child.props.value)

  const showStatus = status === 'danger' && statusMessage
  const isPasswordField = child.props.type === 'password'
  const chars = trackedValue?.length || 0

  if (!isPasswordField && showStrenghtMeter) {
    throw new Error('Strength meter can only be used with a password field')
  }

  const renderHint = () => {
    if (showStatus) {
      return (
        <Feedback id={hintId} variant="critical">
          {statusMessage}
        </Feedback>
      )
    }

    if (description) {
      return (
        <Feedback id={hintId} variant={descriptionVariant || 'default'}>
          {description}
        </Feedback>
      )
    }

    if (child.props.maxLength && !hideCharacterCount) {
      return (
        <Feedback
          id={hintId}
          variant={chars === child.props.maxLength ? 'critical' : 'default'}
        >
          You have {child.props.maxLength - chars} characters left to use
        </Feedback>
      )
    }

    return null
  }

  return (
    <StyledFormField>
      {label && (
        <Label
          htmlFor={fieldId}
          necessity={necessity}
          optionalText={optionalText}
        >
          {label}
        </Label>
      )}
      {instructions && (
        <div id={instructionsId}>
          <MarkdownPreview content={instructions} fontSize="bodySmall" />
        </div>
      )}
      {React.cloneElement(children, {
        id: fieldId,
        status: showStatus ? 'danger' : 'default',
        disabled: disabled,
        'aria-describedby': [instructions && instructionsId, hintId]
          .filter(Boolean)
          .join(' '),
        ...(dataTestid !== undefined && { 'data-testid': dataTestid }),
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (e?.target?.value || e?.type === 'change') {
            setTrackedValue(e.target.value)
          }
          child.props.onChange?.(e)
        },
      })}
      {disabled ? null : renderHint()}
      {isPasswordField && showStrenghtMeter && trackedValue ? (
        <StrenghtMeter text={trackedValue} />
      ) : null}
    </StyledFormField>
  )
}
